'use es6';

import { memo, useCallback, useEffect } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import ImmutablePropTypes from 'react-immutable-proptypes';
import PropTypes from 'prop-types';
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import { WarningIcon, EditIcon } from 'foundations-assets';
import FormattedMessage from 'I18n/components/FormattedMessage';
import styled from 'styled-components';
import { Button } from 'foundations-components/transitional/components/Button';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import PhoneNumberWithProperty from './PhoneNumberWithProperty';
import { getValue, getPropertyName, getObjectId, getMetadata, getValidNumber } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { logCallingError } from 'calling-error-reporting/report/error';
import { PhoneNumberIdentifier } from 'calling-lifecycle-internal/callees/records/PhoneNumberIdentifier';
import { getObjectTypeId } from 'calling-lifecycle-internal/callees/operators/calleesOperators';
import { useAccessLevel } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { HIDDEN } from 'customer-data-objects/property/AccessLevel';
import { HELP_DESK, INBOX } from 'calling-internal-common/constants/appIdentifiers';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledPhoneNumberButton = styled.button.withConfig({
  displayName: "PhoneNumberOption__StyledPhoneNumberButton",
  componentId: "sc-1mpxbaw-0"
})(["background-color:transparent;border:none;cursor:", ";opacity:", ";text-align:start;width:100%;&:hover{background-color:", ";}"], ({
  isDisabled
}) => `${isDisabled ? 'not-allowed' : 'pointer'}`, ({
  isDisabled
}) => `${isDisabled ? '0.5' : '1'}`, ({
  theme
}) => `${theme.color['fill-secondary-subtle']}`);
export function PhoneNumberOption({
  appIdentifier,
  onSelect,
  onEditProperty,
  phoneNumberProperty,
  callableObject,
  editPermissions = null,
  disabled = false,
  OptionIcon = null,
  disabledTooltipMessage,
  formattedName,
  objectTypeId,
  hidePropertyName,
  selectToNumberIndex
}) {
  const phoneNumberValue = getValue(phoneNumberProperty);
  const propertyName = getPropertyName(phoneNumberProperty);
  const phoneNumberMetadata = getMetadata(phoneNumberProperty);
  const isValidNumber = getValidNumber(phoneNumberMetadata);
  const isHelpdesk = appIdentifier === HELP_DESK;
  const isInbox = appIdentifier === INBOX;
  const showInvalidNumber = (isHelpdesk || isInbox) && !isValidNumber;
  const accessLevel = useAccessLevel({
    objectType: objectTypeId,
    propertyName
  });
  useEffect(() => {
    if (!phoneNumberValue) {
      logCallingError({
        errorMessage: 'PhoneNumberOption displayed a number without a value',
        extraData: {
          phoneNumberProperty,
          callableObject
        }
      });
    }
  }, [callableObject, phoneNumberProperty, phoneNumberValue]);
  let isLoadingPermissions = false;
  let canEdit = true;
  let tooltipKey = 'noEditPermissions';
  if (editPermissions) {
    isLoadingPermissions = editPermissions.get('isLoading');
    canEdit = editPermissions.get('canEdit');
    tooltipKey = isLoadingPermissions ? 'loadingPermissions' : tooltipKey;
  }
  const handleSelect = useCallback(() => {
    if (!disabled && !showInvalidNumber && phoneNumberValue) {
      const numberIdentifier = new PhoneNumberIdentifier({
        objectTypeId: getObjectTypeId(callableObject),
        objectId: getObjectId(callableObject),
        propertyName
      });
      onSelect({
        numberIdentifier,
        callableObject
      });
      if (selectToNumberIndex > -1) {
        const action = selectToNumberIndex === 0 ? 'call the first number' : 'call secondary numbers';
        CommunicatorLogger.log('Calling_DropDownInteractions', {
          action,
          source: appIdentifier,
          selectToNumberPosition: selectToNumberIndex + 1
        });
      }
    }
  }, [disabled, showInvalidNumber, phoneNumberValue, callableObject, propertyName, onSelect, selectToNumberIndex, appIdentifier]);
  const handleEditOption = useCallback(evt => {
    evt.stopPropagation();
    onEditProperty(callableObject, phoneNumberProperty);
  }, [callableObject, onEditProperty, phoneNumberProperty]);
  if (accessLevel === HIDDEN) {
    return null;
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: disabledTooltipMessage || 'callee-selection.invalidNumber'
    }),
    "data-selenium-test": "hs-provided-and-call-from-phone-tooltip",
    placement: "bottom",
    disabled: !disabledTooltipMessage && !showInvalidNumber,
    children: /*#__PURE__*/_jsx(StyledPhoneNumberButton, {
      "data-selenium-test": "calling-phone-option",
      onClick: handleSelect,
      "data-number-property": propertyName,
      "data-object-id": getObjectId(callableObject),
      "data-raw-number": phoneNumberValue,
      isDisabled: disabled || showInvalidNumber,
      "aria-label": `Call ${formattedName}`,
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        paddingBlock: "200",
        paddingInline: "300",
        children: [/*#__PURE__*/_jsx(Text, {
          color: "text-core-default",
          children: /*#__PURE__*/_jsxs("div", {
            style: {
              flexGrow: 1
            },
            children: [OptionIcon && /*#__PURE__*/_jsx(OptionIcon, {}), /*#__PURE__*/_jsx(PhoneNumberWithProperty, {
              phoneNumberProperty: phoneNumberProperty,
              hidePropertyName: hidePropertyName
            })]
          })
        }), /*#__PURE__*/_jsxs(View, {
          marginInlineStart: "200",
          as: "div",
          children: [showInvalidNumber && /*#__PURE__*/_jsx(Flex, {
            alignItems: "center",
            "data-selenium-test": "invalid-number-icon",
            children: /*#__PURE__*/_jsx(WarningIcon, {
              size: "300",
              color: "do-not-use-candy-apple"
            })
          }), onEditProperty && /*#__PURE__*/_jsx(Tooltip, {
            disabled: canEdit,
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `callee-selection.phoneNumberErrors.${tooltipKey}`
            }),
            children: /*#__PURE__*/_jsx(Flex, {
              alignItems: "center",
              children: /*#__PURE__*/_jsx(Button, {
                use: "unstyled",
                "data-selenium-test": "edit-number-button",
                onClick: handleEditOption,
                disabled: !canEdit || isLoadingPermissions,
                padding: "0",
                children: /*#__PURE__*/_jsx(EditIcon, {
                  size: "200",
                  color: "do-not-use-eerie"
                })
              })
            })
          })]
        })]
      })
    })
  });
}
PhoneNumberOption.propTypes = {
  appIdentifier: PropTypes.string.isRequired,
  className: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onEditProperty: PropTypes.func,
  editPermissions: ImmutablePropTypes.map,
  disabled: PropTypes.bool,
  OptionIcon: PropTypes.object,
  phoneNumberProperty: RecordPropType('PhoneNumberProperty').isRequired,
  callableObject: RecordPropType('CallableObject').isRequired,
  disabledTooltipMessage: PropTypes.string,
  formattedName: PropTypes.string.isRequired,
  objectTypeId: PropTypes.string.isRequired,
  hidePropertyName: PropTypes.bool,
  selectToNumberIndex: PropTypes.number
};
export default /*#__PURE__*/memo(PhoneNumberOption);